import { AppBaseDto, KeyCredentialDto } from '../api/graph.types';

export const getThumbprint = async (base64String: string): Promise<string> => {
  if (!base64String) return '';

  if (base64String.length < 64) return base64String;

  if (isThumbprintFormat(base64String)) return base64String;

  // Step 1: Decode the base64 string into a Uint8Array (binary data)
  const binaryData = new Uint8Array(
    atob(base64String)
      .split('')
      .map((c) => c.charCodeAt(0))
  );

  // Step 2: Hash the binary data using SHA-1 (or SHA-256, SHA-512, etc.)
  const hashBuffer = await crypto.subtle.digest('SHA-1', binaryData);

  // Step 3: Convert the hash into a readable format (e.g., hex)
  const hashArray = Array.from(new Uint8Array(hashBuffer)); // Convert ArrayBuffer to array
  const thumbprintHex = hashArray.map((byte) => byte.toString(16).padStart(2, '0')).join('');

  return thumbprintHex.toUpperCase(); // Return thumbprint in hexadecimal format
};

export const isSha1 = (str: string) => {
  // Regular expression for SHA-1 (40 hex characters)
  const sha1Regex = /^[a-fA-F0-9]{40}$/;
  return sha1Regex.test(str);
};

export const isSha256 = (str: string) => {
  // Regular expression for SHA-256 (64 hex characters)
  const sha256Regex = /^[a-fA-F0-9]{64}$/;
  return sha256Regex.test(str);
};

export const isThumbprintFormat = (str: string) => {
  return isSha1(str) || isSha256(str);
};

export const arrayBufferToBase64 = (buffer: Uint8Array) => {
  let binary = '';
  const bytes = new Uint8Array(buffer);
  const length = bytes.byteLength;
  for (let i = 0; i < length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary);
};

export const updateCredentials = async (app: AppBaseDto): Promise<void> => {
  if (
    (!app.keyCredentials || app.keyCredentials.length === 0) &&
    (!app.passwordCredentials || app.passwordCredentials.length === 0)
  ) {
    return;
  }

  if (app.keyCredentials && app.keyCredentials.length > 0) {
    await Promise.all(
      app.keyCredentials.map(async (keyCredential: KeyCredentialDto) => {
        if (keyCredential.customKeyIdentifier && isThumbprintFormat(keyCredential.customKeyIdentifier)) {
          keyCredential.thumbprint = keyCredential.customKeyIdentifier;
        } else if (keyCredential.key) {
          keyCredential.thumbprint = await getThumbprint(keyCredential.key);
        }
      })
    );
  }

  // handle the SSO case
  if (app.preferredSingleSignOnMode === 'saml') {
    if (app.keyCredentials && app.keyCredentials.length > 0) {
      const uniqueKeyCredentials = new Map<string, KeyCredentialDto>();

      for (const keyCredential of app.keyCredentials) {
        if (keyCredential.customKeyIdentifier) {
          const existing = uniqueKeyCredentials.get(keyCredential.customKeyIdentifier);
          // Prefer the entry with a key if duplicate exists
          if (!existing && keyCredential.key) {
            uniqueKeyCredentials.set(keyCredential.customKeyIdentifier, keyCredential);
          }
        }
      }

      for (const keyCredential of app.keyCredentials) {
        if (keyCredential.customKeyIdentifier) {
          const existing = uniqueKeyCredentials.get(keyCredential.customKeyIdentifier);
          // Prefer the entry with a key if duplicate exists
          // this handles the entires that are old
          if (!existing) {
            uniqueKeyCredentials.set(keyCredential.customKeyIdentifier, keyCredential);
          }
        }
      }

      app.keyCredentials = Array.from(uniqueKeyCredentials.values());
    }

    if (app.passwordCredentials && app.passwordCredentials.length > 0 && app.keyCredentials) {
      const keyCredentialIdentifiers = new Set(
        app.keyCredentials
          .filter((keyCredential) => keyCredential.customKeyIdentifier)
          .map((keyCredential) => keyCredential.customKeyIdentifier)
      );

      // Filter out passwordCredentials with the same customKeyIdentifier as in keyCredentials
      app.passwordCredentials = app.passwordCredentials.filter(
        (p) => !keyCredentialIdentifiers.has(p.customKeyIdentifier)
      );
    }
  }

  return; // Ensure the function returns a resolved promise
};
